import { AIChatbotEvents, trackAIChatbotEvent } from 'Analytics/ai-chatbot-events';
import { VuMediChatbotModal } from 'Chatbot';
import 'Components/atoms/vm-button-next/vm-button-next';
import 'Components/atoms/vm-icon-next/vm-icon-next';
import VuMediCarousel from 'Utils/vm-carousel';
import SearchAutocomplete from './search-autocomplete';

const MOBILE_BREAKPOINT = 992 - 1;
const NAV_VISIBLE_CLASS = 'is-in-viewport';
const isHemOnc = document.querySelector('body').dataset.siteSlug === 'hematology-and-oncology';

function setupNavigation() {
    const dom = {
        headerContainer: document.querySelector('.js_mainHeader'),
        browseContainer: document.querySelector('.js_navBrowse'),
        browseNavItems: Array.from(document.querySelectorAll('.js_navBrowseDropdownMenu .dropdown-item')),
        profileContainer: document.querySelector('.js_navProfile'),
        profileNavItems: Array.from(document.querySelectorAll('.js_navProfileDropdownMenu .dropdown-item')),
        specialtyContainer: document.querySelector('.js_navSpecialty'),
        specialtyNavItems: Array.from(document.querySelectorAll('.js_navSpecialtiesDropdownMenu .dropdown-item')),
        termsCarousel: document.querySelector('.js_navTermsCarousel'),
        termsTrack: document.querySelector('.js_navTermsTrack'),
        termsContainer: document.querySelector('.js_navTerms'),
        toggleButton: document.querySelector('.js_navToggle'),
        toggleIcon: document.querySelector('.js_navToggle .icon'),
        toggleSearch: document.querySelector('.js_navSearchToggle'),
        navItems: document.querySelectorAll('.js_navTermsCarousel .nav-item .vm-button'),
    };

    const hemOncDom = {
        navigation: document.querySelector('.js_vmNavigation'),
        navItems: document.querySelectorAll('.js_vmNavTermsCarousel > ul > li > .vm-button'),
        browseNavMobileButton: document.querySelector('.js_vmNavBrowseMobile'),
        specialtyNavMobileButton: document.querySelector('.js_vmNavSpecialtiesMobile'),
        profileNavMobileButton: document.querySelector('.js_vmNavProfileButton'),
        navCloseButton: document.querySelector('.js_vmNavCloseButton'),
        termsCarousel: document.querySelector('.js_vmNavTermsCarousel'),
        secondLevel: document.querySelector('.js_vmNavSecondLevel'),
        secondLevelList: document.querySelector('.js_vmNavSecondLevelList'),
        secondLevelBackButton: document.querySelector('.js_vmNavSecondLevelBackButton'),
        secondLevelIntro: document.querySelector('.js_vmNavSecondLevelIntro'),
        secondLevelTitle: document.querySelector('.js_vmNavSecondLevelTitle'),
        secondLevelGroupsWrapper: document.querySelector('.js_vmNavSecondLevelGroupsWrapper'),
        thirdLevel: document.querySelector('.js_vmNavThirdLevel'),
        thirdLevelIntro: document.querySelector('.js_vmNavThirdLevelIntro'),
        thirdLevelList: document.querySelector('.js_vmNavThirdLevelList'),
        thirdLevelBackButton: document.querySelector('.js_vmNavThirdLevelBackButton'),
        thirdLevelTitle: document.querySelector('.js_vmNavThirdLevelTitle'),
        thirdLevelSeeAllButton: document.querySelector('.js_vmNavThirdLevelSeeAllButton'),
        thirdLevelGroupsWrapper: document.querySelector('.js_vmNavThirdLevelGroupsWrapper'),
    };

    const state = {
        visible: false,
    };

    const hideNavigation = () => {
        dom.browseContainer.classList.add('hidden');
        dom.profileContainer.classList.add('hidden');
        dom.specialtyContainer.classList.add('hidden');
        dom.termsContainer?.classList.add('hidden');
        dom.termsTrack.style.transform = 'none';

        dom.toggleIcon.classList.remove('icon-x');
        dom.toggleIcon.classList.add('icon-menu');

        dom.toggleSearch.classList.remove('hidden');
        dom.headerContainer.classList.remove('mobile-open');
        document.body.style.overflow = '';

        state.visible = false;

        if (isHemOnc) {
            hemOncDom.secondLevel?.classList.remove(NAV_VISIBLE_CLASS);
            hemOncDom.thirdLevel?.classList.remove(NAV_VISIBLE_CLASS);
        }
    };

    const removeActiveItemClass = (navItems) => {
        const navItemsArray = Array.from(navItems);
        navItemsArray.find((el) => el.classList.contains('is-active'))?.classList.remove('is-active');
    };

    const showNavigation = (isMobile) => {
        dom.browseContainer.classList.remove('hidden');
        dom.profileContainer.classList.remove('hidden');
        dom.specialtyContainer.classList.remove('hidden');
        dom.termsContainer?.classList.remove('hidden');

        if (!isHemOnc) {
            dom.toggleIcon.classList.remove('icon-menu');
            dom.toggleIcon.classList.add('icon-x');
            dom.toggleSearch.classList.add('hidden');
        } else {
            removeActiveItemClass(hemOncDom.navItems);
            hemOncDom.secondLevel?.classList.remove(NAV_VISIBLE_CLASS);
            hemOncDom.thirdLevel?.classList.remove(NAV_VISIBLE_CLASS);
        }

        if (isMobile) {
            dom.headerContainer.classList.add('mobile-open');
            document.body.style.overflow = 'hidden';
        } else {
            dom.headerContainer.classList.remove('mobile-open');
        }

        state.visible = true;
    };

    const hideHemOncNavigation = (event) => {
        if (!hemOncDom.navigation.contains(event.target)) {
            removeActiveItemClass(hemOncDom.navItems);
            hemOncDom.secondLevel.classList.remove(NAV_VISIBLE_CLASS);
            hemOncDom.thirdLevel.classList.remove(NAV_VISIBLE_CLASS);
            document.removeEventListener('click', hideHemOncNavigation);
        }
    };

    const hemOncSecondaryNavigation = (event, navItems) => {
        const secondLevelItems = navItems.map((el) => {
            return `
                <li>
                    <a class="vm-button vm-button--sm vm-button--tertiary" href="${el.href}" title="${el.title}">
                        ${el.innerText}
                    </a>
                </li>
            `;
        });

        hemOncDom.secondLevelGroupsWrapper.innerHTML = `
            <div class="vm-navigation__second-level-group vm-navigation__second-level-group--specialties">
                <ul class="vm-navigation__second-level-list js_vmNavSecondLevelList">
                    ${secondLevelItems.join('')}
                </ul>
            </div>
        `;
        hemOncDom.secondLevelTitle.innerText =
            event.target.dataset.title || event.target.closest('[data-title]').dataset.title || '';
        hemOncDom.secondLevel.classList.add(NAV_VISIBLE_CLASS);

        // set the value for the 2nd level intro element
        document.documentElement.style.setProperty(
            '--nav-second-level-intro-height',
            hemOncDom.secondLevelIntro.offsetHeight + 'px',
        );
    };

    const hemOncFirstLevelClickHandler = (ev, item) => {
        document.addEventListener('click', hideHemOncNavigation); // setup click outside event

        hemOncDom.secondLevelGroupsWrapper.innerHTML = '';

        const childrenLists = Array.from(item.parentElement.querySelectorAll('.js_vmNavChildrenList'));

        childrenLists.forEach((list) => {
            const listGroupName = list.dataset.typeDisplay || '';
            const listItems = Array.from(list.querySelectorAll('& > li'));
            const itemsMarkup = listItems.map((item) => {
                return `<li>${item.innerHTML}</li>`;
            });

            const groupMarkup = `
                <div class="vm-navigation__second-level-group">
                    <h5>${listGroupName}</h5>
                    <ul class="vm-navigation__second-level-list js_vmNavSecondLevelList">
                        ${itemsMarkup.join('')}
                    </ul>
                </div>
            `;

            hemOncDom.secondLevelGroupsWrapper.innerHTML += groupMarkup;
        });

        // logic for handling the "More" nav item
        // which opens a 2nd level tray on mobile, but a dropdown on desktop
        if (item.classList.contains('vm-button--more')) {
            if (window.innerWidth > MOBILE_BREAKPOINT) {
                removeActiveItemClass(hemOncDom.navItems);
                item.dataset.toggle = 'dropdown';
                item.setAttribute('aria-haspopup', 'true');
                item.setAttribute('aria-expanded', 'false');
                hemOncDom.secondLevel.classList.remove(NAV_VISIBLE_CLASS);
                hemOncDom.thirdLevel.classList.remove(NAV_VISIBLE_CLASS);
                item.nextElementSibling.classList.add('dropdown-menu');
                return;
            } else {
                item.nextElementSibling.classList.remove('dropdown-menu');
                item.nextElementSibling.classList.remove('show');
            }
        }

        if (item.classList.contains('is-active')) {
            item.classList.remove('is-active');
            hemOncDom.secondLevel.classList.remove(NAV_VISIBLE_CLASS);
            hemOncDom.thirdLevel.classList.remove(NAV_VISIBLE_CLASS);
        } else {
            removeActiveItemClass(hemOncDom.navItems);
            item.classList.add('is-active');
            hemOncDom.secondLevelTitle.innerText = item.innerText;
            hemOncDom.secondLevel.classList.add(NAV_VISIBLE_CLASS);

            // set the value for the 2nd level intro element
            document.documentElement.style.setProperty(
                '--nav-second-level-intro-height',
                hemOncDom.secondLevelIntro.offsetHeight + 'px',
            );

            if (window.innerWidth <= MOBILE_BREAKPOINT) {
                item.classList.remove('is-active');
            }
        }
    };

    const hemOnSecondLevelClickHandler = (item) => {
        const grandchildrenLists = Array.from(item.parentElement.querySelectorAll('.js_vmNavGrandchildrenList'));

        hemOncDom.thirdLevelGroupsWrapper.innerHTML = ''; // Clear previous groups

        // construct 3rd level navigation
        if (grandchildrenLists.length) {
            grandchildrenLists.forEach((list) => {
                const listGroupName = list.dataset.typeDisplay || '';
                const listItems = Array.from(list.querySelectorAll('li'));
                const itemsMarkup = listItems.map((item) => {
                    return `<li>${item.innerHTML}</li>`;
                });

                const groupMarkup = `
                    <div class="vm-navigation__third-level-group">
                        <h5>${listGroupName}</h5>
                        <ul class="vm-navigation__third-level-list">
                            ${itemsMarkup.join('')}
                        </ul>
                    </div>
                `;

                hemOncDom.thirdLevelGroupsWrapper.innerHTML += groupMarkup;
            });

            removeActiveItemClass(hemOncDom.secondLevel.querySelectorAll('li button.vm-button'));

            if (window.innerWidth > MOBILE_BREAKPOINT) {
                item.classList.add('is-active');
            }

            hemOncDom.thirdLevel.classList.add(NAV_VISIBLE_CLASS);
            hemOncDom.thirdLevelTitle.innerText = item.innerText;
            hemOncDom.thirdLevelSeeAllButton.href = item.dataset.href;
            hemOncDom.thirdLevelSeeAllButton.innerHTML = `See All from ${item.title} <i class="icon icon-chevron-right"></i>`;

            // set the value for the groups wrapper element
            document.documentElement.style.setProperty(
                '--nav-third-level-groups-intro-height',
                hemOncDom.thirdLevelIntro.offsetHeight + 'px',
            );
        }
    };

    const addDropdownFix = () =>
        dom.termsContainer?.querySelectorAll('.dropdown-menu').forEach((el, idx) => {
            if (idx > 4) {
                el.classList.add('dropdown-menu-right');
            }
        });

    const removeDropdownFix = () =>
        dom.termsContainer?.querySelectorAll('.dropdown-menu').forEach((el) => {
            el.classList.remove('dropdown-menu-right');
        });

    const carousel = new VuMediCarousel(dom.termsCarousel || hemOncDom.termsCarousel);

    // User is not logged in
    if (!dom.toggleButton) {
        return;
    }

    dom.toggleButton.addEventListener('click', (ev) => {
        ev.preventDefault();

        if (state.visible) {
            hideNavigation();
        } else {
            showNavigation(true);
        }
    });

    if (isHemOnc) {
        hemOncDom.navCloseButton.addEventListener('click', () => {
            hideNavigation();
        });

        hemOncDom.navItems.forEach((item) => {
            item.addEventListener('click', (ev) => {
                hemOncFirstLevelClickHandler(ev, item);

                const secondLevelLists = hemOncDom.navigation.querySelectorAll('.js_vmNavSecondLevelList');
                const isMoreButton = item.classList.contains('vm-button--more');

                if (isMoreButton && window.innerWidth > MOBILE_BREAKPOINT) return;

                secondLevelLists.forEach((list) => {
                    const secondLevelItems = list.querySelectorAll('& > li button.vm-button');
                    secondLevelItems.forEach((el) => {
                        el.addEventListener('click', () => {
                            hemOnSecondLevelClickHandler(el);
                        });
                    });

                    // if the first level item is deactivated (2nd/3rd level not shown), stop here
                    if (!item.classList.contains('is-active')) return;

                    // activate the first item in the second level on desktop
                    if (window.innerWidth > MOBILE_BREAKPOINT) {
                        secondLevelItems[0]?.click();
                    }
                });
            });
        });

        hemOncDom.secondLevelBackButton.addEventListener('click', () => {
            hemOncDom.secondLevel.classList.remove(NAV_VISIBLE_CLASS);
        });

        hemOncDom.thirdLevelBackButton.addEventListener('click', () => {
            hemOncDom.thirdLevel.classList.remove(NAV_VISIBLE_CLASS);
        });

        hemOncDom.specialtyNavMobileButton.addEventListener('click', (event) =>
            hemOncSecondaryNavigation(event, dom.specialtyNavItems),
        );
        hemOncDom.browseNavMobileButton.addEventListener('click', (event) =>
            hemOncSecondaryNavigation(event, dom.browseNavItems),
        );
        hemOncDom.profileNavMobileButton.addEventListener('click', (event) => {
            hemOncSecondaryNavigation(event, dom.profileNavItems);
        });
    }

    window.addEventListener('resize', () => {
        if (window.innerWidth > MOBILE_BREAKPOINT) {
            showNavigation(false);
            carousel.initialize();
            addDropdownFix();
        } else {
            hideNavigation();
            carousel.deinitialize();
            removeDropdownFix();
        }
    });

    if (window.innerWidth > MOBILE_BREAKPOINT) {
        showNavigation(false);
        carousel.initialize();
        addDropdownFix();
    }
}

function setupSearch() {
    const dom = {
        headerContainer: document.querySelector('.js_mainHeader'),
        container: document.querySelector('.js_navSearch'),
        input: document.querySelector('.js_navSearch-input'),
        clear: document.querySelector('.js_navSearch-clear'),
        toggleIcon: document.querySelector('.js_navSearchToggle .icon'),
        toggleNav: document.querySelector('.js_navToggle'),
        toggleSearch: document.querySelector('.js_navSearchToggle'),
        submitButton: document.querySelector('.js_navSearch-execute'),
    };

    const state = {
        innerWidth: window.innerWidth,
        visible: false,
    };

    const hideSearch = () => {
        dom.container.classList.add('hidden');
        dom.toggleNav.classList.remove('hidden');
        dom.toggleIcon.classList.add('icon-search');
        dom.toggleIcon.classList.remove('icon-x');
        dom.toggleSearch.classList.remove('opened');
        document.body.style.overflow = '';

        state.visible = false;
    };

    const showSearch = (isMobile) => {
        dom.container.classList.remove('hidden');
        dom.toggleNav.classList.add('hidden');
        dom.toggleIcon.classList.remove('icon-search');
        dom.toggleIcon.classList.add('icon-x');
        dom.toggleSearch.classList.add('opened');

        if (isMobile) {
            dom.input.focus();
            document.body.style.overflow = 'hidden';
        }

        state.visible = true;
    };

    const setupAskmedi = async () => {
        const triggerButtons = document.querySelectorAll('.js_navGlobalAIToggle');
        const siteSlug = triggerButtons[0]?.dataset.site;
        const params = new URLSearchParams({ content_type: 'askmedi' });
        if (siteSlug) params.set('site', siteSlug);
        const response = await fetch(`/api/chats/chat-configuration/?${params}`);

        if (!response.ok || response.status !== 200) {
            return false;
        }

        triggerButtons.forEach((triggerButton) => {
            triggerButton.childNodes.forEach((node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    node.textContent = node.textContent.replace('Ask with AI', 'Ask Vumedi AI');
                }
            });
            triggerButton.classList.remove('is-hidden');
            triggerButton.addEventListener('click', (ev) => {
                ev.preventDefault();
                window.location.href = '/askmedi/';
            });
        });

        trackAIChatbotEvent(AIChatbotEvents.LOADED, {
            assistant_type: 'askmedi',
        });

        return true;
    };

    const setupGlobalAI = async () => {
        if (await setupAskmedi()) {
            return;
        }

        const triggerButtons = document.querySelectorAll('.js_navGlobalAIToggle');
        const chatbot = new VuMediChatbotModal();
        const initialized = await chatbot.initialize();

        if (!initialized || !triggerButtons) {
            return;
        }

        triggerButtons.forEach((triggerButton) => {
            triggerButton.classList.remove('is-hidden');
            triggerButton.addEventListener('click', (ev) => {
                ev.preventDefault();
                chatbot.startAI();
            });
        });
    };

    // User is not logged in
    if (!dom.toggleSearch) {
        return;
    }

    dom.toggleSearch.addEventListener('click', (ev) => {
        ev.preventDefault();

        if (state.visible) {
            hideSearch();
        } else {
            showSearch(true);
        }
    });

    window.addEventListener('resize', () => {
        // Don't act if resolution stayed within the same device type
        if (
            (state.innerWidth < MOBILE_BREAKPOINT && window.innerWidth < MOBILE_BREAKPOINT) ||
            (state.innerWidth > MOBILE_BREAKPOINT && window.innerWidth > MOBILE_BREAKPOINT)
        ) {
            return;
        }

        state.innerWidth = window.innerWidth;

        if (window.innerWidth > MOBILE_BREAKPOINT) {
            showSearch(false);
        } else {
            hideSearch();
        }
    });

    if (window.innerWidth > MOBILE_BREAKPOINT) {
        showSearch(false);
    }

    if (dom.container && dom.input && dom.clear) {
        const autocomplete = new SearchAutocomplete(dom.container, dom.input, dom.clear, dom.submitButton);
        autocomplete.initialize();

        setupGlobalAI();
    }
}

document.addEventListener('DOMContentLoaded', () => {
    setupNavigation();
    setupSearch();
});
